import { Eye } from '@/lib/icons'
import type { BroadcastListItem, BroadcastStatus } from '@ayub/shared'
import { BLOGGER_STAGE_LABELS, formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateUz } from '@/lib/relative-date'
import { cn } from '@/lib/utils'
import { BroadcastHistoryEmptyState } from './EmptyStates'
import { BroadcastErrorBanner, BroadcastRetryState } from './ErrorStates'
import { useBroadcastList } from './hooks'
import { Pagination } from './Pagination'
import { BROADCASTS_PAGE_SIZE } from './api'

const STATUS_META: Record<BroadcastStatus, { label: string; textClass: string; dotClass: string }> =
  {
    pending: { label: 'Navbatda', textClass: 'text-app-hint', dotClass: 'bg-app-hint' },
    sending: {
      label: 'Yuborilmoqda',
      textClass: 'text-urgency-today-text',
      dotClass: 'bg-urgency-today',
    },
    done: { label: 'Yakunlandi', textClass: 'text-urgency-done-text', dotClass: 'bg-urgency-done' },
    failed: {
      label: 'Xato',
      textClass: 'text-urgency-overdue-text',
      dotClass: 'bg-urgency-overdue',
    },
  }

const TEXT_PREVIEW_LENGTH = 60

function previewText(text: string): string {
  if (text.length <= TEXT_PREVIEW_LENGTH) return text
  return `${text.slice(0, TEXT_PREVIEW_LENGTH)}…`
}

/**
 * `targetStage` bo'yicha "kimga" nomi. `manual` audience uchun BAZADA alohida ustun yo'q
 * (`services/broadcast.ts` fayl boshidagi ⭐ izoh: "manual audience bilan yuborilgan
 * broadcast tarixda targetStage: null bo'lib qoladi — vizual jihatdan alldan
 * FARQLANMAYDI"). Shuning uchun bu yerda ikkalasi ham "Barchasi" deb ko'rsatiladi —
 * bu ma'lumot yo'qolishi EMAS, backend cheklovi (server kodidagi izohga qarang), shunchaki
 * tarixdan "aynan kimlarga qo'lda tanlangan edi" deb qayta ajratib bo'lmaydi.
 */
function audienceLabel(item: BroadcastListItem): string {
  return item.targetStage ? BLOGGER_STAGE_LABELS[item.targetStage] : 'Barchasi'
}

export interface BroadcastHistoryProps {
  page: number
  onPageChange: (page: number) => void
  onWatchProgress: (id: number) => void
}

/** "Tarix" bo'limi — `docs/03-app-flow.md`: "12-yan · 'Yangi kitob chiqdi' · 442 ta · 438 yetkazildi". */
export function BroadcastHistory({ page, onPageChange, onWatchProgress }: BroadcastHistoryProps) {
  const { data, isLoading, isFetching, error, refetch } = useBroadcastList(page)
  const items = data?.items ?? []

  if (isLoading) return <BroadcastHistorySkeleton />

  if (error && items.length === 0) {
    return <BroadcastRetryState error={error} onRetry={() => void refetch()} />
  }

  if (items.length === 0) {
    return <BroadcastHistoryEmptyState />
  }

  return (
    // `aria-busy` — fon rejimida qayta so'ralayotganda (`isFetching`) skrinriderga signal
    // beradi, aks holda ro'yxat "jim" yangilanadi (butun ilovada faqat `ui/toast.tsx`da
    // `aria-live` bor edi).
    <div className="flex flex-col gap-3" aria-busy={isFetching}>
      {error && (
        <BroadcastErrorBanner
          error={error}
          isFetching={isFetching}
          onRetry={() => void refetch()}
        />
      )}

      {/* Desktop / tablet: jadval */}
      <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-app-card">
            <tr>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Sana
              </th>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Matn
              </th>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Kimga
              </th>
              <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                Natija
              </th>
              <th className="border-b border-app-border px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} onWatchProgress={onWatchProgress} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: kartochkalar */}
      <div className="flex flex-col gap-2 sm:hidden">
        {items.map((item) => (
          <HistoryMobileCard key={item.id} item={item} onWatchProgress={onWatchProgress} />
        ))}
      </div>

      <Pagination
        page={page}
        pageSize={BROADCASTS_PAGE_SIZE}
        total={data?.total ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function StatusBadge({ status }: { status: BroadcastStatus }) {
  const meta = STATUS_META[status]
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', meta.textClass)}>
      <span className={cn('size-1.5 rounded-full', meta.dotClass)} aria-hidden="true" />
      {meta.label}
    </span>
  )
}

function ResultText({ item }: { item: BroadcastListItem }) {
  const isActive = item.status === 'pending' || item.status === 'sending'
  return (
    <span className="tabular text-app-text">
      {formatNumber(item.totalCount)} ta
      {!isActive && ` · ${formatNumber(item.sentCount)} yetkazildi`}
      {!isActive && item.failedCount > 0 && (
        <span className="text-urgency-overdue-text">
          {' '}
          · {formatNumber(item.failedCount)} yetmadi
        </span>
      )}
    </span>
  )
}

function HistoryRow({
  item,
  onWatchProgress,
}: {
  item: BroadcastListItem
  onWatchProgress: (id: number) => void
}) {
  const isActive = item.status === 'pending' || item.status === 'sending'
  return (
    <tr className="border-b border-app-border last:border-b-0">
      <td className="px-3 py-2 whitespace-nowrap text-app-hint">{formatDateUz(item.createdAt)}</td>
      <td className="px-3 py-2 text-app-text" title={item.textUz}>
        {previewText(item.textUz)}
      </td>
      <td className="px-3 py-2 text-app-hint">{audienceLabel(item)}</td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <ResultText item={item} />
          <StatusBadge status={item.status} />
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {isActive && (
          <Button size="sm" variant="secondary" onClick={() => onWatchProgress(item.id)}>
            <Eye size={14} aria-hidden="true" />
            Kuzatish
          </Button>
        )}
      </td>
    </tr>
  )
}

function HistoryMobileCard({
  item,
  onWatchProgress,
}: {
  item: BroadcastListItem
  onWatchProgress: (id: number) => void
}) {
  const isActive = item.status === 'pending' || item.status === 'sending'
  return (
    <div className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-app-hint">{formatDateUz(item.createdAt)}</span>
        <StatusBadge status={item.status} />
      </div>
      <p className="text-sm text-app-text">{previewText(item.textUz)}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-app-hint">{audienceLabel(item)}</span>
        <ResultText item={item} />
      </div>
      {isActive && (
        <Button size="sm" variant="secondary" onClick={() => onWatchProgress(item.id)}>
          <Eye size={14} aria-hidden="true" />
          Kuzatish
        </Button>
      )}
    </div>
  )
}

function BroadcastHistorySkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        {Array.from({ length: 5 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex h-14 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-[110px]" />
        ))}
      </div>
    </div>
  )
}
